import type { PropsWithChildren } from "react";
import { Children, createElement, memo, useMemo } from "react";

import { addSpaceBetween, generateTypographyClassName } from "./helper";
import type { ITypographyProps } from "./types";
import { typographies } from "./variant";

/**
 * Typography Component
 *
 * This component renders text with different styles based on the props passed to it.
 *
 * It supports different types of text such as headings, paragraphs, and spans.
 *
 * The component also allows for customization of font size, font weight, and color.
 *
 * @param [props.children] - The content to be rendered within the Typography component
 *
 * @returns
 */
export const Typography = memo(function Typography(
  props: PropsWithChildren<ITypographyProps>,
) {
  const {
    variant = "body",
    children,
    className,
    createSpan,
    htmlAttribute,
    ...rest
  } = props;
  const typographySpec = useMemo(
    () => ({ ...typographies[variant], ...rest }),
    [rest, variant],
  );
  const generatedClassName = useMemo(
    () => generateTypographyClassName(typographySpec, className),
    [className, typographySpec],
  );

  const elementType = useMemo(
    () => (createSpan ? "span" : typographySpec.tag),
    [createSpan, typographySpec.tag],
  );

  const childrenCount = useMemo(() => Children.count(children), [children]);

  return createElement(
    elementType,
    { ...htmlAttribute, className: generatedClassName },
    childrenCount > 1 ? addSpaceBetween(children) : children,
  );
});
